import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { AddToQuote, BasketBar } from '@/shared/quotes/AddToQuote';
import { useCan } from '@/shared/session/profiles';
import type { TariffHit, TariffItem } from '@/shared/tariffs/types';
import { tariffDate } from '@/shared/tariffs/types';
import { Card } from '@/shared/ui/Card';
import { CardList, CardListItem } from '@/shared/ui/CardList';
import { Dialog } from '@/shared/ui/Dialog';
import { TBody, THead, Table, Td, Th, Tr } from '@/shared/ui/Table';
import { money, percent, quantity, unitPrice } from '@/shared/utils/money';

import { usePriceSight } from './sight';

/** A column the tariff brings that the platform does not use: the back keeps it as the
 * cell said, text or a number. */
function cellText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function isHit(item: TariffItem): item is TariffHit {
  return 'supplier_name' in item;
}

const MATCH_LABELS: Record<TariffHit['match'], string> = {
  ean: 'Por el EAN',
  reference: 'Referencia exacta',
  prefix: 'La referencia empieza así',
  description: 'Por la descripción',
};

/** «1 ud», «10 uds». Nothing for a tariff that does not say it. */
function perBox(value: string | null | undefined): string {
  if (!value) return '';
  return `${quantity(value)} ${Number(value) === 1 ? 'ud' : 'uds'}/caja`;
}

/** What the price is per, when it is not one: «/ 100 uds». */
function per(value: number | null | undefined): string {
  return value && value !== 1 ? ` / ${value} uds` : '';
}

/**
 * A price of the tariff with what it is per. **Every price of the row is per the same
 * units** (the back takes the price with the discount and the cost from the list or the net
 * price), so a tariff «por 100» says it on each of them: 8,75 alone reads as one screw.
 */
function price(value: string | null | undefined, unit: number | null | undefined): string {
  const formatted = unitPrice(value);
  return formatted === '—' ? formatted : `${formatted}${per(unit)}`;
}

export function ArticlesTable({
  items,
  label,
  onOpen,
  compact = false,
  addable = false,
}: {
  items: TariffItem[];
  label: string;
  onOpen: (item: TariffItem) => void;
  /** Cards only, whatever the width: the column beside a catalog (contract v10.49). */
  compact?: boolean;
  /**
   * The quantity and «Añadir» on each row (contract v10.52), for a profile that makes
   * quotes from the tariffs. Only where every row is of a tariff in force: the back
   * refuses an article of a version already replaced. On a phone the card opens the
   * article, and «Añadir» is there: a button inside a card that is a button is two
   * things under one finger.
   */
  addable?: boolean;
}) {
  const sight = usePriceSight();
  const adding = useCan('tariff_quotes') && addable;
  const withSupplier = items.some(isHit);
  const withFamily = items.some((item) => item.family);

  return (
    <>
      {compact ? null : (
        <div className="hidden md:block">
          <Card padded={false}>
            <Table label={label} minWidth="0">
              <THead>
                <tr>
                  <Th>Artículo</Th>
                  {withSupplier ? <Th className="w-44">Proveedor</Th> : null}
                  <Th className="w-28 text-right">PVP</Th>
                  <Th className="w-24 text-right">Dto. habitual</Th>
                  <Th className="w-28 text-right">Con dto.</Th>
                  {sight.maxDiscount ? <Th className="w-24 text-right">Dto. máximo</Th> : null}
                  {sight.purchase ? <Th className="w-28 text-right">Coste</Th> : null}
                  <Th className="w-28">Caja</Th>
                  {adding ? <Th className="w-40">Al presupuesto</Th> : null}
                </tr>
              </THead>
              <TBody>
                {items.map((item) => (
                  <Tr key={item.id} interactive onClick={() => onOpen(item)}>
                    <Td>
                      <span className="text-ink font-medium tabular-nums">{item.reference}</span>
                      {item.description ? (
                        <span className="text-ink-2 block text-[13.5px]">{item.description}</span>
                      ) : null}
                      {withFamily && item.family ? (
                        <span className="text-ink-3 block text-[12.5px]">{item.family}</span>
                      ) : null}
                    </Td>
                    {withSupplier ? (
                      <Td className="text-ink-2 text-[13.5px]">
                        {isHit(item) ? (
                          <>
                            {item.supplier_name}
                            <span className="text-ink-3 block text-[12.5px]">
                              {item.price_list_name} · {tariffDate(item.tariff_date)}
                            </span>
                          </>
                        ) : null}
                      </Td>
                    ) : null}
                    <Td className="text-right tabular-nums">
                      {price(item.list_price, item.price_unit)}
                    </Td>
                    <Td className="text-ink-2 text-right tabular-nums">
                      {percent(item.usual_discount_pct)}
                    </Td>
                    <Td className="text-ink text-right font-medium tabular-nums">
                      {price(item.price_with_discount, item.price_unit)}
                    </Td>
                    {sight.maxDiscount ? (
                      <Td className="text-ink-2 text-right tabular-nums">
                        {percent(item.max_discount_pct)}
                      </Td>
                    ) : null}
                    {sight.purchase ? (
                      <Td className="text-ink-2 text-right tabular-nums">
                        {price(item.cost, item.price_unit)}
                      </Td>
                    ) : null}
                    <Td className="text-ink-3 text-[13px]">{perBox(item.units_per_box)}</Td>
                    {adding ? (
                      // The whole cell, padding included: a click that misses «Añadir» by a
                      // hair is not a click on the row.
                      <Td className="cursor-default" onClick={(event) => event.stopPropagation()}>
                        <AddToQuote
                          itemId={item.id}
                          reference={item.reference}
                          supplier={isHit(item) ? item.supplier_name : undefined}
                          quotable={item.quotable}
                        />
                      </Td>
                    ) : null}
                  </Tr>
                ))}
              </TBody>
            </Table>
          </Card>
        </div>
      )}

      <CardList label={label} className={compact ? '' : 'md:hidden'}>
        {items.map((item) => (
          <CardListItem
            key={item.id}
            title={
              <>
                <span className="tabular-nums">{item.reference}</span>
                {item.description ? (
                  <span className="text-ink-2 block font-normal">{item.description}</span>
                ) : null}
              </>
            }
            subtitle={isHit(item) ? item.supplier_name : (item.family ?? undefined)}
            aside={
              // The price to give: with the usual discount when the supplier has one, the list
              // price when not (no conditions yet is the normal start, and «—» there reads as
              // an article without a price).
              <span className="text-ink text-[14px] font-medium tabular-nums">
                {price(item.price_with_discount ?? item.list_price, item.price_unit)}
              </span>
            }
            footer={
              <>
                <span>
                  PVP {price(item.list_price, item.price_unit)}
                  {item.usual_discount_pct ? ` · dto. ${percent(item.usual_discount_pct)}` : ''}
                </span>
                {sight.maxDiscount && item.max_discount_pct ? (
                  <span>Máx. {percent(item.max_discount_pct)}</span>
                ) : null}
                {sight.purchase && item.cost ? (
                  <span>Coste {price(item.cost, item.price_unit)}</span>
                ) : null}
              </>
            }
            onClick={() => onOpen(item)}
          />
        ))}
      </CardList>
    </>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="border-line flex items-baseline justify-between gap-4 border-b py-1.5 last:border-b-0">
      <dt className="text-ink-3 text-[13px]">{label}</dt>
      <dd className="text-ink text-right text-[14px] tabular-nums">{children}</dd>
    </div>
  );
}

/** Everything about one article: what the table has no room for. With `addable`, and
 * the profile for it, «Añadir» to the draft the bar names (contract v10.52). */
export function ArticleDialog({
  item,
  onClose,
  addable = false,
}: {
  item: TariffItem;
  onClose: () => void;
  addable?: boolean;
}) {
  const sight = usePriceSight();
  const adding = useCan('tariff_quotes') && addable;
  const hit = isHit(item) ? item : null;
  const extra = sight.purchase ? Object.entries(item.extra ?? {}) : [];

  return (
    <Dialog
      title={item.reference}
      description={item.description ?? undefined}
      onClose={onClose}
      size="md"
    >
      <dl className="flex flex-col">
        {hit ? (
          <Row label="Proveedor">
            {hit.supplier_name} · {hit.price_list_name}
          </Row>
        ) : null}
        {hit ? <Row label="Fecha de la tarifa">{tariffDate(hit.tariff_date)}</Row> : null}
        {item.ean ? <Row label="EAN">{item.ean}</Row> : null}
        {item.family ? <Row label="Familia">{item.family}</Row> : null}
        <Row label="PVP de tarifa">{price(item.list_price, item.price_unit)}</Row>
        <Row label="Descuento habitual">{percent(item.usual_discount_pct)}</Row>
        <Row label="Precio con el descuento habitual">
          {price(item.price_with_discount, item.price_unit)}
        </Row>
        {sight.maxDiscount ? (
          <Row label="Descuento máximo">{percent(item.max_discount_pct)}</Row>
        ) : null}
        {sight.purchase ? (
          <Row label="Precio neto">{price(item.net_price, item.price_unit)}</Row>
        ) : null}
        {sight.purchase ? (
          <Row label="Coste para Cubas">{price(item.cost, item.price_unit)}</Row>
        ) : null}
        {item.units_per_box ? (
          <Row label="Unidades por caja">{quantity(item.units_per_box)}</Row>
        ) : null}
        {hit?.free_shipping_from ? (
          <Row label="Porte gratis desde">{money(hit.free_shipping_from)}</Row>
        ) : null}
        {extra.map(([key, value]) => (
          <Row key={key} label={key}>
            {cellText(value)}
          </Row>
        ))}
      </dl>
      {adding ? (
        <section aria-label="Añadir al presupuesto" className="mt-4 flex flex-col gap-2">
          <BasketBar />
          <AddToQuote
            itemId={item.id}
            reference={item.reference}
            supplier={hit?.supplier_name}
            quotable={item.quotable}
          />
        </section>
      ) : null}
      {hit ? (
        <p className="text-ink-3 mt-3 text-[13px]">
          {MATCH_LABELS[hit.match]}.{' '}
          <Link className="link" to={`/documentos/tarifas/${hit.version_id}`}>
            Ver la tarifa entera
          </Link>
        </p>
      ) : null}
    </Dialog>
  );
}
